import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { Subject, filter, take, takeUntil } from 'rxjs';
import { AuthService } from '../../../services/auth.service';
import { AdPerformanceDto, MarketingAttributionService } from '../../../services/marketing-attribution.service';
import { PaginationComponent } from '../../shared/pagination/pagination.component';
import { DateInputComponent } from '../../shared/date-input/date-input.component';
import { AdLeaderboardComponent } from '../acquisition/ad-leaderboard/ad-leaderboard.component';
import type { AcquisitionDateRange } from '../acquisition/acquisition.component';
import { formatLocalDate, validateAcquisitionRange } from '../acquisition/acquisition-range';

export type CampaignsView = 'leaderboard' | 'previous';

/**
 * Marketing → Campaigns. Shows the acquisition ad leaderboard (both CTAs, daily spend, attribution
 * window). The previous WhatsApp-only table stays one click away while the leaderboard is
 * reconciled against Ads Manager; it's removed with the old ad-performance endpoint afterwards.
 */
@Component({
  selector: 'app-campaigns',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, PaginationComponent, DateInputComponent, AdLeaderboardComponent],
  templateUrl: './campaigns.component.html',
  styleUrl: './campaigns.component.css'
})
export class CampaignsComponent implements OnInit, OnDestroy {
  view: CampaignsView = 'leaderboard';
  rangeError = '';
  /** The leaderboard's range; replaced (not mutated) on Apply so the child sees a change. */
  appliedRange: AcquisitionDateRange | null = null;

  ads: AdPerformanceDto[] = [];
  directRow: AdPerformanceDto | null = null;
  totalCount = 0;
  loading = false;
  errorMessage = '';

  fromDate = '';
  toDate = '';
  searchText = '';

  currentPage = 1;
  pageSize = 10;

  private destroy$ = new Subject<void>();

  constructor(
    private marketingAttributionService: MarketingAttributionService,
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);

    this.toDate = this.formatDateForInput(today);
    this.fromDate = this.formatDateForInput(thirtyDaysAgo);
    this.applyRange();
  }

  /** Switches view; the previous table loads on first use only. */
  selectView(view: CampaignsView): void {
    if (view === this.view) {
      return;
    }
    this.view = view;
    if (view === 'previous') {
      this.authService.getMsalInitializedObservable()
        .pipe(filter(initialized => initialized), take(1), takeUntil(this.destroy$))
        .subscribe(() => this.loadAds());
    }
  }

  /** Applies the date range to whichever view is showing. */
  applyRange(): void {
    if (this.view === 'previous') {
      this.applyFilters();
      return;
    }
    this.rangeError = validateAcquisitionRange(this.fromDate, this.toDate);
    if (!this.rangeError) {
      this.appliedRange = { from: this.fromDate, to: this.toDate };
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadAds(): void {
    this.loading = true;
    this.errorMessage = '';

    const from = this.fromDate ? new Date(this.fromDate + 'T00:00:00').toISOString() : undefined;
    const to = this.toDate ? new Date(this.toDate + 'T23:59:59').toISOString() : undefined;
    const search = this.searchText.trim() || undefined;

    this.marketingAttributionService.getAdPerformance(from, to, search, this.currentPage - 1, this.pageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.ads = response.items || [];
          this.directRow = response.directTraffic ?? null;
          this.totalCount = response.totalCount;
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading ad performance:', error);
          this.loading = false;
          this.ads = [];
          this.directRow = null;
          this.totalCount = 0;
          if (error.status === 401) {
            this.errorMessage = 'Authentication required. Please log in again.';
          } else if (error.status === 403) {
            this.errorMessage = 'Access denied. You do not have permission to view campaign performance.';
          } else {
            this.errorMessage = 'Failed to load campaign performance. Please try again.';
          }
        }
      });
  }

  applyFilters(): void {
    this.currentPage = 1;
    this.loadAds();
  }

  clearFilters(): void {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    this.toDate = this.formatDateForInput(today);
    this.fromDate = this.formatDateForInput(thirtyDaysAgo);
    this.searchText = '';
    this.currentPage = 1;
    this.loadAds();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadAds();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.loadAds();
  }

  get totalPages(): number {
    return Math.ceil(this.totalCount / this.pageSize) || 1;
  }

  private formatDateForInput(date: Date): string {
    return formatLocalDate(date);
  }
}
